import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, AlertTriangle, Check, X, Shield, RotateCcw } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';
import { useSfx } from '../../../store/sfxStore';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "What is Phishing?",
        icon: "🎣",
        content: (
            <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-2xl border-2 border-blue-200">
                    <h3 className="font-bold text-xl text-blue-800 mb-3">Fishing for Your Info</h3>
                    <p className="text-slate-700 mb-4">
                        <strong>Phishing</strong> is when bad people pretend to be someone you trust
                        (like a bank or a friend) to trick you into giving them your personal information.
                    </p>
                    <p className="text-slate-700">
                        Just like a fisherman uses bait to catch fish, scammers use fake emails
                        and websites to "catch" your passwords!
                    </p>
                </div>
                <div className="bg-red-50 p-5 rounded-xl border border-red-200">
                    <h4 className="font-bold text-red-800 mb-3">🚨 What Scammers Want:</h4>
                    <ul className="text-red-700 space-y-2 text-sm">
                        <li className="flex items-center gap-2"><X size={16} /> Your passwords</li>
                        <li className="flex items-center gap-2"><X size={16} /> Credit card numbers</li>
                        <li className="flex items-center gap-2"><X size={16} /> Your home address</li>
                    </ul>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "Spot the Warning Signs",
        icon: "🔍",
        content: (
            <div className="space-y-4">
                <p className="text-slate-600 mb-4">Learn to recognize these RED FLAGS:</p>
                <div className="grid gap-4 text-sm">
                    <div className="bg-white p-4 rounded-xl border-2 border-red-200 shadow-sm">
                        <div className="font-bold text-red-600 mb-1">⚠️ Strange Email Address</div>
                        <p className="text-slate-600">support@amaz<span className="bg-yellow-200 px-1 rounded font-bold">0</span>n-deals.net (uses 0 instead of o)</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl border-2 border-red-200 shadow-sm">
                        <div className="font-bold text-red-600 mb-1">⚠️ Scary Language</div>
                        <p className="text-slate-600">"ACT NOW or your account will be DELETED!"</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl border-2 border-red-200 shadow-sm">
                        <div className="font-bold text-red-600 mb-1">⚠️ Too Good To Be True</div>
                        <p className="text-slate-600">"You won $1,000,000! Click here!"</p>
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "The SAFE Method",
        icon: "🛡️",
        content: (
            <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                    <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                        <div className="font-bold text-green-700 mb-1">1. STOP 🛑</div>
                        <p className="text-slate-600 text-xs">Don't click links immediately. Breathe.</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                        <div className="font-bold text-green-700 mb-1">2. CHECK 🔍</div>
                        <p className="text-slate-600 text-xs">Examine the email address closely.</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                        <div className="font-bold text-green-700 mb-1">3. ASK 🙋</div>
                        <p className="text-slate-600 text-xs">Ask an adult if you are unsure.</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                        <div className="font-bold text-green-700 mb-1">4. DELETE 🗑️</div>
                        <p className="text-slate-600 text-xs">If it's fake, trash it.</p>
                    </div>
                </div>
                <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200 text-center">
                    <h4 className="font-bold text-yellow-800 mb-1">Game Time!</h4>
                    <p className="text-yellow-700 text-sm">
                        Identify at least 3 emails correctly to pass.
                    </p>
                </div>
            </div>
        )
    }
];

// ============ GAME LOGIC ============
interface Email {
    id: number;
    from: string;
    subject: string;
    preview: string;
    isPhishing: boolean;
    clues: string[];
}

const EMAILS: Email[] = [
    {
        id: 1,
        from: 'support@amaz0n-deals.net',
        subject: '🚨 YOUR ACCOUNT WILL BE CLOSED!!!',
        preview: 'Dear Customer, Click here NOW or lose your account forever...',
        isPhishing: true,
        clues: ['Weird spelling (amaz0n with zero)', 'Scary words to rush you', 'Strange website (.net not .com)'],
    },
    {
        id: 2,
        from: 'teacher@school.edu',
        subject: 'Homework for Monday',
        preview: 'Hi class! Remember to finish your reading by Monday...',
        isPhishing: false,
        clues: ['Real school email (.edu)', 'Normal message', 'No scary words'],
    },
    {
        id: 3,
        from: 'winner@free-pr1zes.xyz',
        subject: 'YOU WON $1,000,000!!!',
        preview: 'Congratulations! Send us your address to claim your prize...',
        isPhishing: true,
        clues: ['Too good to be true!', 'Weird website (.xyz)', 'Asks for your info'],
    },
    {
        id: 4,
        from: 'mom@gmail.com',
        subject: 'Pick up from practice',
        preview: 'I will pick you up at 5pm. Love you!',
        isPhishing: false,
        clues: ['Real email from family', 'Normal message', 'No links or tricks'],
    },
    {
        id: 5,
        from: 'security@bank-verify.co',
        subject: 'Verify your password NOW',
        preview: 'Your bank account is locked. Enter your password here...',
        isPhishing: true,
        clues: ['Banks never ask for passwords!', 'Fake website name', 'Trying to scare you'],
    },
];

const SafetyGame = ({ onComplete }: { onComplete: () => void }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [answered, setAnswered] = useState<boolean | null>(null);
    const [completedAll, setCompletedAll] = useState(false);
    const { play } = useSfx();

    const currentEmail = EMAILS[currentIndex];

    const handleAnswer = (userSaysPhishing: boolean) => {
        const correct = userSaysPhishing === currentEmail.isPhishing;
        setAnswered(correct);
        if (correct) {
            setScore(s => s + 1);
            play('success');
        } else {
            play('error');
        }
    };

    const nextEmail = () => {
        if (currentIndex < EMAILS.length - 1) {
            setCurrentIndex(i => i + 1);
            setAnswered(null);
        } else {
            setCompletedAll(true);
            if (score >= 2) { // Allow for a couple mistakes, but ensure they learned
                play('glory');
                onComplete();
            } else {
                play('error');
            }
        }
    };

    const reset = () => {
        setCurrentIndex(0);
        setScore(0);
        setAnswered(null);
        setCompletedAll(false);
    };

    if (completedAll) {
        return (
            <div className="text-center py-12">
                {score >= 3 ? (
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                        <div className="text-6xl mb-4">🏆</div>
                        <h2 className="text-3xl font-black text-slate-800 mb-2">Great Job!</h2>
                        <p className="text-slate-600 mb-6 text-xl">
                            You spotted {score}/{EMAILS.length} correctly!
                        </p>
                        <div className="bg-green-50 p-6 rounded-2xl mx-auto max-w-sm mb-6">
                            <p className="text-green-700 font-bold">Module Complete!</p>
                            <p className="text-green-600 text-sm">You are now a certified Scam Spotter.</p>
                        </div>
                    </motion.div>
                ) : (
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                        <div className="text-6xl mb-4">🤔</div>
                        <h2 className="text-3xl font-black text-slate-800 mb-2">Keep Practicing</h2>
                        <p className="text-slate-600 mb-6 text-xl">
                            You got {score}/{EMAILS.length}. Try to get at least 3 correct!
                        </p>
                        <button
                            onClick={() => { reset(); play('click'); }}
                            onMouseEnter={() => play('hover')}
                            className="bg-blue-500 text-white px-8 py-3 rounded-xl font-bold hover:bg-blue-600 shadow-lg"
                        >
                            <RotateCcw className="inline mr-2" size={20} /> Try Again
                        </button>
                    </motion.div>
                )}
            </div>
        );
    }

    return (
        <div className="max-w-xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <div className="text-sm font-bold text-slate-400 uppercase tracking-widest">
                    Inbox ({currentIndex + 1}/{EMAILS.length})
                </div>
                <div className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold">
                    Score: {score}
                </div>
            </div>

            <AnimatePresence mode="wait">
                <motion.div
                    key={currentEmail.id}
                    initial={{ x: 50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: -50, opacity: 0 }}
                    className="bg-white rounded-3xl shadow-xl overflow-hidden mb-8 border-2 border-slate-100"
                >
                    {/* Email Header */}
                    <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
                        <div className="flex items-center gap-3">
                            <div className="bg-white p-2 rounded-full border border-slate-200">
                                <Mail className="text-slate-400" size={24} />
                            </div>
                            <div>
                                <div className="font-bold text-slate-800 text-sm break-all">{currentEmail.from}</div>
                                <div className="text-xs text-slate-500 uppercase font-bold mt-0.5">Subject: {currentEmail.subject}</div>
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-8">
                        <p className="text-slate-600 text-lg leading-relaxed font-serif">
                            "{currentEmail.preview}"
                        </p>
                    </div>
                </motion.div>
            </AnimatePresence>

            {answered === null ? (
                <div className="grid grid-cols-2 gap-4">
                    <button
                        onClick={() => { handleAnswer(false); play('click'); }}
                        onMouseEnter={() => play('hover')}
                        className="bg-green-500 text-white py-4 rounded-2xl font-bold text-xl hover:bg-green-600 flex items-center justify-center gap-2 shadow-lg transition-transform hover:scale-105 active:scale-95"
                    >
                        <Check size={24} /> Safe
                    </button>
                    <button
                        onClick={() => { handleAnswer(true); play('click'); }}
                        onMouseEnter={() => play('hover')}
                        className="bg-red-500 text-white py-4 rounded-2xl font-bold text-xl hover:bg-red-600 flex items-center justify-center gap-2 shadow-lg transition-transform hover:scale-105 active:scale-95"
                    >
                        <AlertTriangle size={24} /> Scam
                    </button>
                </div>
            ) : (
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className={`p-6 rounded-2xl ${answered ? 'bg-green-100 border-2 border-green-300' : 'bg-red-100 border-2 border-red-300'}`}
                >
                    <div className="flex items-center gap-3 mb-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${answered ? 'bg-green-500' : 'bg-red-500'}`}>
                            {answered ? <Check className="text-white" /> : <X className="text-white" />}
                        </div>
                        <div className={`text-xl font-black ${answered ? 'text-green-800' : 'text-red-800'}`}>
                            {answered ? 'Correct!' : 'Oops!'}
                        </div>
                    </div>

                    <p className="font-bold text-slate-700 mb-4">
                        This email was: <span className={currentEmail.isPhishing ? 'text-red-600' : 'text-green-600'}>
                            {currentEmail.isPhishing ? 'A SCAM 🚨' : 'SAFE ✅'}
                        </span>
                    </p>

                    <div className="bg-white/60 rounded-xl p-4 mb-4">
                        <div className="text-xs font-bold text-slate-500 uppercase mb-2">Clues you missed:</div>
                        <ul className="space-y-1">
                            {currentEmail.clues.map((clue, i) => (
                                <li key={i} className="text-slate-700 text-sm flex items-center gap-2">
                                    <span className="text-blue-500">•</span> {clue}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <button
                        onClick={() => { nextEmail(); play('click'); }}
                        onMouseEnter={() => play('hover')}
                        className="w-full bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 shadow-lg"
                    >
                        Next Email →
                    </button>
                </motion.div>
            )}
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const InternetSafety = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        setCompleted(true);
        completeModule('internet-safety', 100);
    };

    return (
        <LessonWrapper
            moduleName="Internet Safety"
            moduleEmoji="🛡️"
            moduleColor="blue"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Scam Spotter"
        >
            <SafetyGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default InternetSafety;
